import React from "react";
import { Link } from "react-router-dom";
import { PublicHeader } from "../components/cod/PublicHeader";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ChicagoStars, CODLogoSVG } from "../components/cod/Logo";
import { Seo } from "../components/cod/Seo";
import { CheckCircle2, Briefcase, Users, FileCheck, ArrowRight } from "lucide-react";
import { IMG } from "../lib/images";

export function Employers() {
  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="For Employers"
        description="Hire credential-ready candidates through Chicago Opportunity Desk. Pre-screened, certified talent for CNA, PCT, security, food service, and skilled trades — with no placement fees."
        path="/employers"
        schema="ProfessionalService"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "For Employers", path: "/employers" }]}
      />
      <PublicHeader/>
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
          <p className="cod-eyebrow">For Employers</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 mt-3">Talent that's credentialed, vetted, and ready.</h1>
            <div className="cod-rule-red mt-5"/>
            <p className="mt-5 text-lg text-slate-700 max-w-2xl">COD provides Chicago employers a pipeline of candidates who hold required credentials (CPR, Food Handler, ServSafe Alcohol, Security PERC, and more), have completed Fingerprinting and Background Checks where applicable, and arrive with a COD Case Manager assigned for retention support.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/onboarding/employer">
                <Button size="lg" className="bg-[var(--cod-red)] hover:bg-[#A40D26] text-white text-base px-8 py-6" data-testid="employer-post-hiring-request">
                  Post a Hiring Request <ArrowRight size={16} className="ml-2"/>
                </Button>
              </Link>
              <a href="#how-it-works" className="inline-flex">
                <Button size="lg" variant="outline" className="text-base px-8 py-6 border-[var(--cod-navy)] text-[var(--cod-navy)] hover:bg-[var(--cod-navy-soft)]">
                  See how it works
                </Button>
              </a>
            </div>
          </div>
          <div className="md:col-span-5 hidden md:block">
            <div className="rounded-lg overflow-hidden border-4 border-[var(--cod-navy)] shadow-md">
              <img src={IMG.interview} alt="Chicago employer interviewing" loading="lazy" decoding="async" className="w-full h-72 object-cover"/>
            </div>
          </div>
        </div>
      </section>
      <main id="how-it-works" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-3 gap-6">
        {[
          {icon: Briefcase, t:"Post a role", d:"Share your hiring needs with COD. We match candidates from active pathways.", cta: "Post a role", to: "/onboarding/employer", testid:"emp-card-post"},
          {icon: FileCheck, t:"Receive pre-vetted candidates", d:"Each candidate arrives with credentials documented, background cleared (where applicable), and a COD point of contact."},
          {icon: Users, t:"Retention support", d:"Your new hire's COD Case Manager continues to provide stability and barrier-removal support post-hire."},
        ].map(b => (
          <Card key={b.t} className="cod-card-elev"><CardContent className="pt-6">
            <div className="w-10 h-10 rounded-md flex items-center justify-center mb-3" style={{background:"var(--cod-navy-soft)"}}>
              <b.icon size={20} style={{color:"var(--cod-navy)"}}/>
            </div>
            <h3 className="font-serif text-xl text-slate-900">{b.t}</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{b.d}</p>
            {b.cta && b.to && (
              <Link to={b.to} data-testid={b.testid} className="inline-block mt-3 text-sm font-semibold text-[var(--cod-red)] hover:underline">{b.cta} →</Link>
            )}
          </CardContent></Card>
        ))}
      </main>
      <section className="cod-section" style={{background:"var(--cod-navy-soft)"}}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl text-slate-900">Hire with COD.</h2>
          <p className="text-slate-700 mt-4">Become a Chicago Opportunity Desk hiring partner. Complete our 5-minute self-serve onboarding to create your account and post your first job order — no phone call required.</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/onboarding/employer" data-testid="emp-cta-onboard"><Button size="lg" className="bg-[var(--cod-red)] hover:bg-[#A40D26] text-white px-8 py-7">Become a hiring partner</Button></Link>
            <Link to="/contact" data-testid="emp-cta-contact"><Button size="lg" variant="outline" className="px-8 py-7">Prefer to talk? Contact us</Button></Link>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-600">
            <span>📞 <a href="tel:+13123434183" className="cod-link">(312) 343-4183</a></span>
            <span>✉️ <a href="mailto:info@joinCOD.org" className="cod-link">info@joinCOD.org</a></span>
            <span>📍 Chicago, IL</span>
          </div>
        </div>
      </section>
    </div>
  );
}

export function Partners() {
  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="For Community Partners"
        description="Community organizations partner with Chicago Opportunity Desk for bidirectional referrals, warm handoffs, and shared outcome tracking. Extend your reach without duplicating services."
        path="/partners"
        schema="ProfessionalService"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "For Partners", path: "/partners" }]}
      />
      <PublicHeader/>
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
          <p className="cod-eyebrow">For Partner Organizations</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 mt-3">Shared outcomes for Chicago residents.</h1>
            <div className="cod-rule-red mt-5"/>
            <p className="mt-5 text-lg text-slate-700 max-w-2xl">COD partners with community-based organizations, government agencies, healthcare systems, training providers, and behavioral-health networks. Together we deliver coordinated, warm, end-to-end navigation across credentials, compliance, business, and stability services.</p>
          </div>
          <div className="md:col-span-5 hidden md:block">
            <div className="rounded-lg overflow-hidden border-4 border-[var(--cod-navy)] shadow-md">
              <img src={IMG.community} alt="Community organization partners" loading="lazy" decoding="async" className="w-full h-72 object-cover"/>
            </div>
          </div>
        </div>
      </section>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="font-serif text-2xl text-slate-900 mb-6">What partnership looks like</h2>
        <div className="cod-rule-red mb-8"/>
        <ul className="space-y-3 max-w-2xl">
          {[
            "Bidirectional referrals with warm handoffs (not just emails)",
            "Shared COD Case Manager touchpoints and outcome confirmation",
            "Co-branded materials for residents you serve",
            "Quarterly outcomes review and cohort planning",
            "Direct access to COD's Government Contract Accelerator network where appropriate",
          ].map(t => (
            <li key={t} className="flex gap-3 text-slate-800"><CheckCircle2 size={20} style={{color:"var(--cod-red)"}} className="mt-0.5 flex-shrink-0"/> {t}</li>
          ))}
        </ul>
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link to="/onboarding/partner" data-testid="par-cta-onboard"><Button size="lg" className="bg-[var(--cod-red)] hover:bg-[#A40D26] text-white px-8 py-7">Become a COD partner</Button></Link>
          <Link to="/contact" data-testid="par-cta-contact"><Button size="lg" variant="outline" className="px-8 py-7">Prefer to talk? Contact us</Button></Link>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-slate-600">
          <span>📞 <a href="tel:+13123434183" className="cod-link">(312) 343-4183</a></span>
          <span>✉️ <a href="mailto:info@joinCOD.org" className="cod-link">info@joinCOD.org</a></span>
          <span>📍 Chicago, IL</span>
        </div>
        <p className="mt-4 text-xs text-slate-500 max-w-xl">Community organizations, training providers, government agencies, and funders — complete our 5-minute self-serve onboarding to set up your organization profile immediately. No phone call required.</p>
      </main>
    </div>
  );
}

export function About() {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Seo
        title="About Us"
        description="Chicago Opportunity Desk is a mission-driven Opportunity Center dedicated to connecting individuals, families, entrepreneurs, employers, and community partners to meaningful opportunities."
        path="/about"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "About COD", path: "/about" }]}
      />
      <PublicHeader/>

      {/* Page header */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <p className="cod-eyebrow">About Chicago Opportunity Desk</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 mt-3">Connecting people to opportunity.</h1>
          <div className="cod-rule-red mt-5"/>
          <p className="mt-5 text-lg text-slate-700 max-w-3xl">A mission-driven Opportunity Center serving Chicago residents, families, entrepreneurs, employers, and community partners.</p>
        </div>
      </section>

      {/* ABOUT — organization first */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-10 text-slate-700 leading-relaxed" data-testid="about-org-section">
        <section>
          <p className="cod-eyebrow mb-2">About Chicago Opportunity Desk</p>
          <h2 className="font-serif text-3xl text-slate-900 mb-5">Who we are</h2>
          <p className="text-lg">Chicago Opportunity Desk (COD) is a mission-driven Opportunity Center dedicated to connecting individuals, families, entrepreneurs, employers, and community partners to meaningful opportunities that create lasting economic mobility.</p>

          <p className="text-lg mt-6">Our mission is simple:</p>
          <blockquote className="mt-3 border-l-4 pl-4 py-1" style={{borderColor:"var(--cod-red)"}}>
            <p className="font-serif text-xl text-slate-900">To connect people to opportunity by identifying and removing the barriers that prevent success.</p>
          </blockquote>

          <p className="text-lg mt-6">We believe that opportunity should never depend on where someone lives, who they know, or whether they understand how to navigate complex systems. Too often, people are forced to visit multiple organizations to obtain employment assistance, certifications, business resources, public benefits, housing support, or workforce guidance. Chicago Opportunity Desk was created to eliminate that fragmentation by serving as a single point of entry where people can access coordinated services designed around their goals.</p>
        </section>

        <div className="cod-rule-red"/>

        <section>
          <h2 className="font-serif text-2xl text-slate-900 mb-4">A coordinated structure for connecting people to opportunity</h2>
          <p className="text-lg">Chicago Opportunity Desk operates with a nonprofit arm that expands our ability to provide workforce development, education, community services, and other mission-driven initiatives. Together, our business and nonprofit operations create a coordinated structure for connecting people, employers, organizations, and communities to opportunity.</p>
        </section>

        <div className="cod-rule-red"/>

        <section>
          <h2 className="font-serif text-2xl text-slate-900 mb-4">What we do</h2>
          <p className="text-lg">Our services include workforce navigation, career coaching, job placement assistance, resume development, interview preparation, CPR/BLS certification, First Aid certification, Food Handler certification, Food Manager certification, ServSafe Alcohol certification, Security Training, fingerprinting, background checks, employment documentation assistance, PERC assistance, Certified Nursing Assistant (CNA) pathways, Patient Care Technician (PCT) pathways, Phlebotomy pathways, EKG Technician pathways, Medical Coding &amp; Billing pathways, Home Care Aide pathways, Government Contract Accelerator services, business formation support, procurement readiness, capability statements, vendor registration assistance, housing navigation, public benefits navigation, behavioral health navigation, and coordinated community resource referrals.</p>
          <p className="text-lg mt-5">Whether someone is pursuing their first job, advancing an existing career, earning a required certification, starting a business, expanding into government contracting, hiring qualified employees, or seeking community resources, Chicago Opportunity Desk is designed to provide a clear and coordinated pathway forward.</p>
        </section>

        <div className="cod-rule-red"/>

        <section>
          <h2 className="font-serif text-2xl text-slate-900 mb-4">Our vision</h2>
          <p className="text-lg">Our vision is to become Chicago's most trusted Opportunity Center by creating a connected ecosystem where residents, employers, businesses, educators, government agencies, and community organizations work together to expand opportunity, strengthen neighborhoods, and create lasting economic impact.</p>
        </section>

        <section>
          <div className="rounded-md p-6 border border-slate-200" style={{background:"var(--cod-navy-soft)"}}>
            <p className="text-lg text-slate-900">At Chicago Opportunity Desk, we don't simply provide services.</p>
            <p className="font-serif text-2xl text-slate-900 mt-3">We build pathways.</p>
            <p className="font-serif text-2xl text-slate-900">We remove barriers.</p>
            <p className="font-serif text-2xl text-slate-900">We connect people to opportunity.</p>
          </div>
        </section>
      </main>

      {/* EXECUTIVE LEADERSHIP — organization remains primary */}
      <section className="bg-white border-t border-slate-200" data-testid="about-leadership-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="mb-10">
            <p className="cod-eyebrow mb-2">Executive Leadership</p>
            <h2 className="font-serif text-3xl text-slate-900">The team building Chicago Opportunity Desk</h2>
            <div className="cod-rule-red mt-4"/>
          </div>

          {/* Featured Founder card */}
          <div className="grid md:grid-cols-12 gap-8 lg:gap-12 items-start mb-16" data-testid="about-founder-section">
            <div className="md:col-span-4 flex md:block justify-center">
              <div className="relative w-48 sm:w-56 md:w-full max-w-xs">
                <div
                  className="aspect-square rounded-full overflow-hidden ring-4 ring-white shadow-lg"
                  style={{background:"var(--cod-navy-soft)", boxShadow:"0 8px 24px rgba(15,23,42,0.12)"}}
                  data-testid="founder-portrait"
                >
                  <img
                    src="/keith-coleman-headshot.png"
                    alt="Keith Coleman, Founder of Chicago Opportunity Desk"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div className="mt-4 text-center md:text-left">
                  <p className="font-serif text-lg text-slate-900">Keith Coleman</p>
                  <p className="text-sm text-slate-600">Founder, Chicago Opportunity Desk</p>
                </div>
              </div>
            </div>

            <div className="md:col-span-8 text-slate-700 leading-relaxed">
              <p className="cod-eyebrow mb-2">Our Founder</p>
              <h3 className="font-serif text-2xl text-slate-900 mb-5">A letter from Keith Coleman</h3>
              <p className="text-lg">My name is Keith Coleman. I'm 39 years old, a Black man raised on the southeast side of Chicago — in the Jeffrey Manor community, on 100th and Paxton. I know the everyday realities of inner-city neighborhoods because I grew up living them.</p>
              <p className="text-lg mt-5">I built the Chicago Opportunity Desk because I believe opportunity belongs inside the communities that need it most. When knowledge, credentials, and resources are placed directly in the neighborhoods they serve, communities have the chance to change from within.</p>
              <p className="text-lg mt-5">That change begins with financial stability — credentials that lead to employment, and honest support for the financial, physical, and emotional barriers that keep people from reaching their potential.</p>
              <p className="text-lg mt-5">My professional background is in behavioral health and grant writing, with the last decade in real estate. Across every chapter of that work, one belief has stayed constant: <strong className="text-slate-900">every person in Chicago has the ability to become anything they choose, provided they have the right tools.</strong></p>
              <p className="text-lg mt-5">That is what Chicago Opportunity Desk exists to provide.</p>
              <p className="text-base text-slate-600 italic mt-6">— Keith Coleman, Founder</p>
            </div>
          </div>

          {/* Executive team row of 3 */}
          <div className="border-t border-slate-200 pt-12">
            <p className="cod-eyebrow mb-6 text-center md:text-left">Executive Team</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 lg:gap-12" data-testid="leadership-row">
              {[
                { name: "Tekei Wynn",      title: "Governance & Compliance Officer", src: "/tekei-wynn-headshot.png", testid: "leader-tekei" },
              ].map((leader) => (
                <div key={leader.name} className="text-center" data-testid={leader.testid}>
                  <div
                    className="aspect-square rounded-full overflow-hidden mx-auto w-40 sm:w-48 lg:w-56 ring-4 ring-white flex items-center justify-center"
                    style={{background:"var(--cod-navy-soft)", boxShadow:"0 8px 24px rgba(15,23,42,0.10)"}}
                  >
                    {leader.src ? (
                      <img
                        src={leader.src}
                        alt={`${leader.name}, ${leader.title}`}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-center px-4" data-testid={`${leader.testid}-placeholder`}>
                        <CODLogoSVG size={56}/>
                        <p className="mt-3 text-xs uppercase tracking-wider font-semibold" style={{color:"var(--cod-navy)"}}>Official photo<br/>coming soon</p>
                      </div>
                    )}
                  </div>
                  <p className="font-serif text-lg text-slate-900 mt-5">{leader.name}</p>
                  <p className="text-sm text-slate-600 mt-1">{leader.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
        <p className="text-sm text-slate-500 italic pt-4 border-t border-slate-200">You are remembered. Something will move forward today.</p>
      </div>
    </div>
  );
}

export function Contact() {
  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <Seo
        title="Contact"
        description="Reach Chicago Opportunity Desk by phone at (312) 343-4183 or email info@joinCOD.org. Monday–Friday, 9am–6pm Central. Serving Chicago residents, employers, and community partners."
        path="/contact"
        schema="LocalBusiness"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}
        faq={[
          { q: "How quickly will I hear back?", a: "Phone calls and voicemails are returned the same business day. Emails receive a response within one business day. If you flagged a P1 or P2 urgency inside the Navigator, a COD Case Manager will reach out same-day or the next business morning." },
          { q: "Do I need to pay to speak with someone?", a: "No. Reaching out, speaking with the Opportunity Navigator, and starting a plan with a COD Case Manager are always free. Some certification and business services have costs, but eligibility for subsidized or funded seats is discussed up front." },
          { q: "Can I bring my own documents?", a: "Yes. You can upload documents (resume, ID, prior certifications, background clearance letters) securely from your account after signing up." },
          { q: "I'm in a crisis situation right now.", a: "If you are in immediate danger, call 911. If you're facing a housing loss, utility shutoff, court date within 48 hours, or a similar urgent situation, please call (312) 343-4183 during business hours or start a Navigator conversation and the system will alert a Case Manager immediately." },
          { q: "I'm an employer with an open role.", a: "Email info@joinCOD.org with the role, credentials required, and shift structure. A COD relationship manager will reach out within two business days to align on candidate fit." },
          { q: "I'm a community partner interested in a MOU.", a: "Email info@joinCOD.org with a brief description of your organization and the population you serve. We'll schedule a partnership discovery call within one business week." },
        ]}
      />
      <PublicHeader/>
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <p className="cod-eyebrow">Contact</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 mt-3">We're here when you need us.</h1>
          <div className="cod-rule-red mt-5"/>
          <p className="mt-5 text-lg text-slate-700 max-w-3xl">Whether you're a Chicago resident exploring a new career, an employer looking for credential-ready talent, or a community partner planning bidirectional referrals — the fastest way to reach the Chicago Opportunity Desk team is right here.</p>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Direct contact block */}
        <div className="grid md:grid-cols-3 gap-6 mb-14">
          <Card className="cod-card-elev" data-testid="contact-card-phone">
            <CardContent className="pt-6">
              <p className="cod-eyebrow mb-1">Call the desk</p>
              <p className="font-serif text-2xl text-slate-900 mt-1">(312) 343-4183</p>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">Speak with a COD team member during business hours. If we're on another call, leave a voicemail — we return every message the same business day.</p>
            </CardContent>
          </Card>
          <Card className="cod-card-elev" data-testid="contact-card-email">
            <CardContent className="pt-6">
              <p className="cod-eyebrow mb-1">Email us</p>
              <a href="mailto:info@joinCOD.org" className="font-serif text-2xl mt-1 block underline" style={{color:"var(--cod-navy)"}} data-testid="contact-email">info@joinCOD.org</a>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">General inquiries, partnership questions, employer intake, and media requests. Please expect a response within 1 business day.</p>
            </CardContent>
          </Card>
          <Card className="cod-card-elev" data-testid="contact-card-hours">
            <CardContent className="pt-6">
              <p className="cod-eyebrow mb-1">Hours &amp; location</p>
              <p className="font-serif text-2xl text-slate-900 mt-1">Chicago, IL</p>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">Monday–Friday · 9:00 AM – 6:00 PM Central. Appointments recommended for in-person visits.</p>
            </CardContent>
          </Card>
        </div>

        {/* Fastest path */}
        <section className="grid md:grid-cols-2 gap-10 items-start mb-16">
          <div>
            <p className="cod-eyebrow mb-2">The fastest path forward</p>
            <h2 className="font-serif text-3xl text-slate-900 mb-4">Start with the Opportunity Navigator.</h2>
            <div className="cod-rule-red mb-5"/>
            <p className="text-lg text-slate-700 leading-relaxed">Most people who reach out to Chicago Opportunity Desk get to a next step faster by starting a Navigator conversation. The Navigator asks a few plain-language questions, remembers your answers, and hands off to a real COD Case Manager the moment a human is needed. You never have to explain your situation twice.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/start"><Button className="bg-[var(--cod-red)] hover:bg-[#A40D26] text-white px-6 py-6" data-testid="contact-cta-start">Start with the Navigator</Button></Link>
              <Link to="/marketplace"><Button variant="outline" className="px-6 py-6" data-testid="contact-cta-marketplace">Browse the Marketplace</Button></Link>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 p-6" style={{background:"var(--cod-navy-soft)"}} data-testid="contact-audience-block">
            <h3 className="font-serif text-xl text-slate-900 mb-4">Who reaches out to COD?</h3>
            <ul className="space-y-3 text-slate-700">
              <li className="flex gap-3"><CheckCircle2 size={20} style={{color:"var(--cod-red)"}} className="mt-0.5 flex-shrink-0"/> <span><strong className="text-slate-900">Chicago residents</strong> exploring careers, certifications, business formation, or navigation to housing, benefits, and behavioral health.</span></li>
              <li className="flex gap-3"><CheckCircle2 size={20} style={{color:"var(--cod-red)"}} className="mt-0.5 flex-shrink-0"/> <span><strong className="text-slate-900">Employers</strong> ready to hire pre-screened, credentialed candidates with continued retention support after placement.</span></li>
              <li className="flex gap-3"><CheckCircle2 size={20} style={{color:"var(--cod-red)"}} className="mt-0.5 flex-shrink-0"/> <span><strong className="text-slate-900">Community partners</strong> looking to build a bidirectional referral relationship instead of duplicating services.</span></li>
              <li className="flex gap-3"><CheckCircle2 size={20} style={{color:"var(--cod-red)"}} className="mt-0.5 flex-shrink-0"/> <span><strong className="text-slate-900">Instructors and educators</strong> teaching under a COD-affiliated certification pathway.</span></li>
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section data-testid="contact-faq">
          <p className="cod-eyebrow mb-2">Frequently asked</p>
          <h2 className="font-serif text-3xl text-slate-900 mb-4">Before you reach out</h2>
          <div className="cod-rule-red mb-8"/>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {q:"How quickly will I hear back?", a:"Phone calls and voicemails are returned the same business day. Emails receive a response within one business day. If you flagged a P1 or P2 urgency inside the Navigator, a COD Case Manager will reach out same-day or the next business morning."},
              {q:"Do I need to pay to speak with someone?", a:"No. Reaching out, speaking with the Opportunity Navigator, and starting a plan with a COD Case Manager are always free. Some certification and business services have costs, but eligibility for subsidized or funded seats is discussed up front."},
              {q:"Can I bring my own documents?", a:"Yes. You can upload documents (resume, ID, prior certifications, background clearance letters) securely from your account after signing up. This lets your Case Manager move faster on your behalf."},
              {q:"I'm in a crisis situation right now.", a:"If you are in immediate danger, call 911. If you're facing a housing loss, utility shutoff, court date within 48 hours, or a similar urgent situation, please call (312) 343-4183 during business hours or start a Navigator conversation and the system will alert a Case Manager immediately."},
              {q:"I'm an employer with an open role.", a:"Email info@joinCOD.org with the role, credentials required, and shift structure. A COD relationship manager will reach out within two business days to align on candidate fit."},
              {q:"I'm a community partner interested in a MOU.", a:"Email info@joinCOD.org with a brief description of your organization and the population you serve. We'll schedule a partnership discovery call within one business week."},
            ].map(item => (
              <div key={item.q} className="p-5 rounded-md border border-slate-200 bg-white">
                <h3 className="font-serif text-lg text-slate-900">{item.q}</h3>
                <p className="text-sm text-slate-700 mt-2 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Explore more */}
        <section className="mt-16 pt-10 border-t border-slate-200">
          <p className="cod-eyebrow mb-2">Explore more</p>
          <h2 className="font-serif text-2xl text-slate-900 mb-5">Places to go next</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <Link to="/about" className="p-4 border border-slate-200 rounded-md hover:border-[var(--cod-navy)] hover:bg-[var(--cod-navy-soft)] transition-colors" data-testid="contact-link-about">
              <p className="font-serif text-lg text-slate-900">About COD</p>
              <p className="text-slate-600 mt-1">Our mission, the two independent organizations behind COD, and Executive Leadership team.</p>
            </Link>
            <Link to="/pathways" className="p-4 border border-slate-200 rounded-md hover:border-[var(--cod-navy)] hover:bg-[var(--cod-navy-soft)] transition-colors" data-testid="contact-link-pathways">
              <p className="font-serif text-lg text-slate-900">Pathways</p>
              <p className="text-slate-600 mt-1">Healthcare, business, and workforce pathways in the right sequence.</p>
            </Link>
            <Link to="/employers" className="p-4 border border-slate-200 rounded-md hover:border-[var(--cod-navy)] hover:bg-[var(--cod-navy-soft)] transition-colors" data-testid="contact-link-employers">
              <p className="font-serif text-lg text-slate-900">For employers</p>
              <p className="text-slate-600 mt-1">Hire credentialed talent with retention support built in.</p>
            </Link>
            <Link to="/partners" className="p-4 border border-slate-200 rounded-md hover:border-[var(--cod-navy)] hover:bg-[var(--cod-navy-soft)] transition-colors" data-testid="contact-link-partners">
              <p className="font-serif text-lg text-slate-900">For partners</p>
              <p className="text-slate-600 mt-1">Bidirectional referrals and shared outcome tracking for CBOs.</p>
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
